import React, { useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { TextField } from "@mui/material";
import { getUserDetails, userProfilePictureRemove, userProfilePictureUpdate, userProfileUpdate } from '../../api/casp/user';
import { toast } from 'react-toastify';
import { withTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from "yup";
import _ from "lodash"
import ImageUploading from "react-images-uploading";
import { AcceptProfileFileType } from '../../constants/Common';
import ImageCropper from './ImageCropper';
import { dataURLtoFile, getUrl } from '../../helpers/file_helper';
import SweetAlert from "react-bootstrap-sweetalert";
import Loader from '../Common/Loader';
import UserProfile from '../casp/common/userProfile';
import { editProfile } from '../../store/actions';
import { useDispatch, useSelector } from "react-redux";
import { Avatar } from "@mui/material";
import { MemberIcon } from '../../constants/MemberIcon';

const ProfileDetailDrawer = ({ isOpenProfileDetail, toggle, t }) => {
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [userDetails, setUserDetails] = useState();
  const [croppedImage, setCroppedImage] = useState(null);
  const [profilePicture, setProfilePicture] = useState();
  const loggedInUser = useSelector((state) => state.Profile.user);
  const [isLoading, setIsLoading] = useState(false);
  const [userObject, setUserObject] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNo: "",
    department: "",
    address: {
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    profilePicture: '',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [profile, setProfile] = useState();
  const [isOpenSweetAlert, setIsOpenSweetAlert] = useState(false);
  const dispatch = useDispatch();

  const phoneRegExp = /^\+?[0-9]([0-9]|(-(?!-)))*[0-9]$/;
  const formikValidation = Yup.object().shape({
    firstName: Yup.string()
      .strict(true)
      .trim(t('Must not include leading and trailing spaces'))
      .max(250, t("First name can not be greater than 250 characters"))
      .required(t("Please enter first name")),
    lastName: Yup.string()
      .strict(true)
      .trim(t('Must not include leading and trailing spaces'))
      .max(250, t("Last name can not be greater than 250 characters"))
      .required(t("Please enter last name")),
    email: Yup.string()
      .strict(true)
      .trim(t('Must not include leading and trailing spaces'))
      .email(t("Please enter valid email"))
      .required(t("Please enter email")),
    contactNo: Yup.string()
      .strict(true)
      .required(t("Please enter contact no"))
      .matches(phoneRegExp, t("Contact no is not valid")),
    department: Yup.string()
      .strict(true)
      .trim(t('Must not include leading and trailing spaces'))
      .optional(),
    address: Yup.object()
      .shape({
        addressLine: Yup.string()
          .strict(true)
          .trim(t('Must not include leading and trailing spaces'))
          .optional(),
        city: Yup.string()
          .strict(true)
          .trim(t('Must not include leading and trailing spaces'))
          .optional(),
        state: Yup.string()
          .strict(true)
          .trim(t('Must not include leading and trailing spaces'))
          .optional(),
        zipCode: Yup.string()
          .strict(true).trim(t('Must not include leading and trailing spaces'))
          .min(4, t('Zip code should be 4 digits'))
          .optional(),
        country: Yup.string()
          .strict(true)
          .trim(t('Must not include leading and trailing spaces'))
          .optional(),
      })
      .optional()
  });

  const formik = useFormik({
    initialValues: {
      ...userObject,
    },
    validationSchema: formikValidation,
    validateOnMount: true,
  });

  useEffect(() => {
    fetchUserDetails()
  }, [])

  const fetchUserDetails = async () => {
    try {
      setIsLoading(true)
      const res = await getUserDetails(loggedInUser.id)
      if (res.statusCode === 200) {
        setUserDetails(res.data.data)
        const data = {
          firstName: res.data.data?.firstName ?? '',
          lastName: res.data.data?.lastName ?? '',
          email: res.data.data?.email ?? '',
          contactNo: res.data.data?.contactNo ?? '',
          gender: res.data.data?.gender?? '',
          addressId: res.data.data?.addressId,
          status: res.data.data?.status?? '',
          address: {
            city: res.data.data?.address?.city ?? '',
            state: res.data.data?.address?.state ?? '',
            country: res.data.data?.address?.country ?? '',
            zipCode: res.data.data?.address?.zipCode?.toString() ?? '',
            addressLine: res.data.data?.address?.addressLine ?? ''
          },
          department: res.data.data.department ? res.data.data.department : '',
          profilePicture: res.data.data.profilePicture,
        }
        setProfilePicture(res.data.data.profilePicture)
        formik.setValues(data)
        setUserObject(data)
      }
      setIsLoading(false)
    } catch (e) {
      toast.error(e)
    }
  }

  const openEditProfileDrawer = () => {
    setIsEditDrawerOpen(true)
  }

  const closeEditProfileDrawer = () => {
    setIsEditDrawerOpen(false)
    toggle(false)
    formik.resetForm();
    formik.setValues(formik.initialValues);
  }
  const handleEdit = async () => {
    try {
      const data = _.omit(userObject, 'profilePicture')
      setIsLoading(true);
      if (croppedImage) {
        await handleProfilePictureEdit(data)
      }
      if(!croppedImage && userObject.profilePicture && !profilePicture ){
        await handleProfilePictureRemove(data)
      }
      if (!_.isEqual(data, _.omit(formik.values, 'profilePicture'))) {
        const address = {
          ...(formik.values.address.addressLine !== '' && {
            addressLine: formik.values.address.addressLine
          }),
          ...(formik.values.address.zipCode !== '' && {
            zipCode: formik.values.address.zipCode
          }),
          ...(formik.values.address.state !== '' && {
            state: formik.values.address.state
          }),
          ...(formik.values.address.city !== '' && {
            city: formik.values.address.city
          }),
          ...(formik.values.address.country !== '' && {
            country: formik.values.address.country
          }),
        }
        const response = await userProfileUpdate(userDetails?.id, {
          firstName: formik.values.firstName,
          lastName: formik.values.lastName,
          ...((formik.values.department.trim()) && {
            department: formik.values.department
          }),
          ...(!_.isEmpty(address) && {
            address,
          }),
          contactNo: formik.values.contactNo,
          companyId: userDetails?.company.id,
          ...(userDetails?.addressId && {
            addressId: userDetails?.addressId
          })
        });
        if (response.statusCode === 200) {
          toast.success(response.data.message);
          setIsLoading(false);
          closeEditProfileDrawer();
          let user = loggedInUser
          user.name = response.data.data.name
          dispatch(editProfile(user))
        }
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfilePictureEdit = async (data) => {
    try {
      const formData = new FormData();
      const file = dataURLtoFile(croppedImage, profile[0].file.name)
      formData.append('file', file);
      const response = await userProfilePictureUpdate(userDetails.id, formData);
      if (response.statusCode === 200) {
        if (_.isEqual(data, _.omit(formik.values, 'profilePicture'))) {
          toast.success(response.data.message);
          closeEditProfileDrawer();
        }
        fetchUserDetails()
        let user = loggedInUser
        user.profilePicture = response.data.data.profilePicture
        dispatch(editProfile(user))
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleProfilePictureRemove = async (data) => {
    try {
      const response = await userProfilePictureRemove(userDetails.id);

      if (response.statusCode === 200) {
        if (_.isEqual(data, _.omit(formik.values, 'profilePicture'))) {
        toast.success(response.data.message);
        closeEditProfileDrawer();
        }
        fetchUserDetails()
        let user = loggedInUser
        user.profilePicture = null
        dispatch(editProfile(loggedInUser))
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleClose = () => {
    setDialogOpen(false)
  };

  return (
    <>
      <Drawer anchor="right" open={isOpenProfileDetail} onClose={() => toggle(false)}className='tenantDetailsDrawer viewProDetails'>
        <div className='responsiveDrawer'>
          {!isLoading && userDetails &&
            <div className='p-3'>
              <div className='profileDrawerHeader d-flex justify-content-between'>
                <h5>{t('Profile Details')}</h5>
                <Button type="button" variant="outlined" size="small" onClick={openEditProfileDrawer}><EditOutlinedIcon />{t('Edit')}</Button>
                <CloseIcon className="crossIcon" onClick={() => toggle(false)} />
              </div>
              <div className='profileDrawerContent p-2'>
                <div className="mb-3 row align-items-center justify-content-center">
                  <div className="d-flex flex-column align-items-center ">
                    <div className="custom-image-upload">
                      <div className="image-placeholder position-relative">
                        <UserProfile user={userDetails} avatarClassName="profileAvatar" />
                      </div>
                    </div>
                    <div className="text-center mt-2">
                      <div className='pro_person_name mb-0'>{t(userDetails?.name)}</div>
                      <div className="designation">{t(userDetails?.department)}</div>
                    </div>

                  </div>
                </div>
                <div className="mb-3 row pr_border">
                  <div className="col-sm-6 p-2">
                    <div className='person_contact pr_border_right'>
                      <h6 className='pro_title'><LocalPhoneOutlinedIcon /> {t('Phone Number')}</h6>
                      <span className='pr_person_contact'>
                        {t(userDetails?.contactNo)}
                      </span>
                    </div>
                  </div>
                  <div className="col-sm-6 p-2">
                    <div className='person_contact ps-3'>
                      <h6 className='pro_title'><MailOutlineOutlinedIcon /> {t('Email')}</h6>
                      <span className='pr_person_contact'>
                        {t(userDetails?.email)}
                      </span>
                    </div>
                  </div>
                  <div className="col-sm-12 pr_border pr_border_bottom p-2">
                    <h6 className='pro_title'><AccountCircleOutlinedIcon /> {t('Gender')} </h6>
                    <span className='pr_person_contact'>{userDetails?.gender} </span>
                    <h6 className='pro_title'>{t('Status')} </h6>
                    <span className='pr_person_contact'>{userDetails?.status} </span>
                  </div>
                  <div className="col-sm-12 pr_border pr_border_bottom p-2">
                    <h6 className='pro_title'><RoomOutlinedIcon /> {t('Address')} </h6>
                    <span className='pr_person_contact'>{userDetails?.address ? `${t(userDetails?.address?.addressLine) + ', ' + t(userDetails?.address?.city) + ', ' + t(userDetails?.address?.state) + ', ' + t(userDetails?.address?.country) + ', ' + t(userDetails?.address?.zipCode)} ` : ''} </span>
                  </div>
                 
                </div>
              </div>
            </div>
          }
          {isLoading && !userDetails &&
            <Loader />
          }
        </div>
      </Drawer>

      {isEditDrawerOpen &&
        <Drawer anchor="right" open={isEditDrawerOpen} onClose={closeEditProfileDrawer} className='tenantDetailsDrawer viewProDetails'>
          <div className='responsiveDrawer p-3'>
            <div className='profileDrawerHeader d-flex justify-content-between'>
              <h5>{t('Edit Profile')}</h5>
              <CloseIcon className="crossIcon" onClick={closeEditProfileDrawer} />
            </div>
            <div className='profileDrawerContentprofileDrawerContentEdit'>
              <div className="custom-image-upload d-flex justify-content-center mb-2">
                <div className="image-placeholder position-relative">
                  {!croppedImage && !profilePicture &&
                    <Avatar
                      className='profileAvatar' {...MemberIcon(userDetails?.name ? userDetails?.name : '')}
                    />
                  }
                  {(croppedImage || profilePicture) &&
                    <img
                      src={croppedImage ?? getUrl(profilePicture)}
                      alt=''
                      className={`rounded-circle header-profile-user`}
                    />
                  }
                  <ImageUploading
                    value={profile}
                    onChange={(newImage) => {
                      setDialogOpen(true);
                      setProfile(newImage);
                    }}
                    acceptType={AcceptProfileFileType}
                    maxFileSize={1024 * 1024 * 10}
                  >
                    {({ onImageUpload, onImageUpdate, errors }) => (
                      <>
                        <label htmlFor="profile_photo" className="editIconBox position-absolute">
                          <EditIcon onClick={profile ? onImageUpload : () => onImageUpdate(0)}
                          />
                        </label>
                        {errors && <div>
                          {errors.acceptType && toast.error('Your selected file type is not allow')}
                          {errors.maxFileSize && toast.error('Selected file size exceed maximum file size: 10 MB')}
                        </div>
                        }
                      </>
                    )}
                  </ImageUploading>
                </div>
              </div>
              {profilePicture &&
                <div className="text-center mb-2">
                  <Button
                    color="primary"
                    onClick={() => {
                      setIsOpenSweetAlert(true)
                    }}
                  >
                    {t('Remove Picture')}
                  </Button>
                </div>
              }
              <div className="row">
                <div className="col-sm-6 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("First Name")}
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    name="firstName"
                    value={t(formik.values?.firstName)}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                    required
                  />
                  {formik.touched.firstName && formik.errors.firstName && (
                    <span className="text-danger">{formik.errors.firstName}</span>
                  )}
                </div>
                <div className="col-sm-6 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("Last Name")}
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    name="lastName"
                    value={formik.values?.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                    required
                  />
                  {formik.touched.lastName && formik.errors.lastName && (
                    <span className="text-danger">{formik.errors.lastName}</span>
                  )}
                </div>
                <div className="col-sm-12 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("Email")}
                    variant="outlined"
                    type="email"
                    size="small"
                    fullWidth
                    value={t(userDetails?.email)}
                    disabled
                  />
                </div>
                <div className="col-sm-6 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("Phone")}
                    variant="outlined"
                    type="phone"
                    size="small"
                    fullWidth
                    required
                    name="contactNo"
                    value={formik.values?.contactNo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                  />
                  {formik.touched.contactNo && formik.errors.contactNo && (
                    <span className="text-danger">{formik.errors.contactNo}</span>
                  )}
                </div>
                <div className="col-sm-6 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("Department")}
                    variant="outlined"
                    type="phone"
                    size="small"
                    fullWidth
                    name="department"
                    value={formik.values?.department}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                  />
                  {formik.touched.department && formik.errors.department && (
                    <span className="text-danger">{formik.errors.department}</span>
                  )}
                </div>
                <div className="col-sm-12 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("Address Line")}
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    name="address.addressLine"
                    value={formik.values?.address?.addressLine}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                  />
                  {formik?.touched?.address?.addressLine &&
                    formik?.errors?.address?.addressLine && (
                      <span className="text-danger">
                        {formik.errors.address.addressLine}
                      </span>
                    )}
                </div>
                {/* This would be dropdown */}
                <div className="col-sm-6 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("City")}
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    name="address.city"
                    value={formik.values?.address?.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                  />
                  {formik?.touched?.address?.city &&
                    formik?.errors?.address?.city && (
                      <span className="text-danger">
                        {formik.errors.address.city}
                      </span>
                    )}
                </div>
                {/* This would be dropdown */}
                <div className="col-sm-6 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("State")}
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    name="address.state"
                    value={formik.values?.address?.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                  />
                  {formik?.touched?.address?.state &&
                    formik?.errors?.address?.state && (
                      <span className="text-danger">
                        {formik.errors.address.state}
                      </span>
                    )}
                </div>
                <div className="col-sm-6 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("Zip Code")}
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    name="address.zipCode"
                    value={formik.values?.address?.zipCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                  />
                  {formik?.touched?.address?.zipCode &&
                    formik?.errors?.address?.zipCode && (
                      <span className="text-danger">
                        {formik.errors.address.zipCode}
                      </span>
                    )}
                </div>
                <div className="col-sm-6 mb-3">
                  <TextField
                    id="outlined-basic"
                    label={t("Country")}
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    name="address.country"
                    value={formik.values?.address?.country}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                  />
                  {formik?.touched?.address?.country &&
                    formik?.errors?.address?.country && (
                      <span className="text-danger">
                        {formik.errors.address.country}
                      </span>
                    )}
                </div>
                <div className="plsubmitButton">
                  <Button
                    className="btn addNew_ProjectBtn"
                    type="submit"
                    disabled={isLoading || !formik.isValid || (!croppedImage && _.isEqual(_.omit(userObject, 'profilePicture'), _.omit(formik.values, 'profilePicture')) && (userObject.profilePicture === profilePicture))}
                    onClick={handleEdit}
                  >
                    {t('Submit')}
                  </Button>
                </div>
              </div>
            </div>
          </div>


          {isOpenSweetAlert && (
            <SweetAlert
              title="Are you sure want to remove your profile picture?"
              warning
              showCancel
              confirmButtonText="Yes, delete it!"
              confirmBtnBsStyle="success"
              cancelBtnBsStyle="danger"
              onConfirm={() => {
                setCroppedImage()
                setProfilePicture()
                setIsOpenSweetAlert(false);
              }}
              onCancel={() => {
                setIsOpenSweetAlert(false)
              }}>
            </SweetAlert>
          )}
        </Drawer>
      }
      {dialogOpen &&
        <ImageCropper
          open={dialogOpen}
          handleClose={handleClose}
          image={profile && profile?.length > 0 && profile[0].dataURL}
          userId={userDetails.id}
          onComplete={(imagePromisse) => {
            imagePromisse.then((image) => {
              setDialogOpen(false);
              setCroppedImage(image)
              // handleProfilePictureEdit(image)
            });
          }}
          containerStyle={{
            position: "relative",
            width: "100%",
            height: 300,
            background: "#333"
          }}
        />
      }

    </>
  );
}

export default withTranslation()(ProfileDetailDrawer)
